import React, { createContext, useContext, useMemo } from 'react';
import ChannelContext from '../components/ChannelContext';
import { useShopConfig } from '../hooks/useShopConfig';
import { gtagTracker } from './integrations/gtag';
import { gtmTracker } from './integrations/gtm';
import { referrerModifier } from './integrations/gtm/referrerModifier';
import { relewareTracker } from './integrations/releware';
import { createServerTracker, trackerRegistry } from './trackerRegistry';
import { Helmet } from 'react-helmet-async';
import { usePreconnectLinks } from '../hooks/usePreconnectLinks';
import { ga4Tracker } from './integrations/ga4';
export const defaultConsentSettings = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    personalization_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500
};
export const CONSENT_COOKIE_NAME = 'GoogleTrackingConsents';
function isMSExternal(external) {
    return 'msTrackingProtectionEnabled' in external;
}
const initialAnalyticsConfig = {
    ga: {
        trackingId: '',
        useArticleNumberAsId: false,
        additionalGtagTrackingIds: []
    },
    ga4: {
        trackingId: '',
        useArticleNumberAsId: false
    },
    gtm: { containerId: '', useGa4Events: false },
    releware: {
        enabled: false,
        baseUrl: null,
        uid: null
    },
    trackingAllowed: false,
    consentedToTracking: false,
    cookies: undefined,
    useTrackingConsentAPI: false
};
export const AnalyticsContext = createContext({
    config: initialAnalyticsConfig,
    trackerRegistry: null
});
export const getFacebookCapi = (selectedChannel) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    let token, pixelId;
    if (((_c = (_b = (_a = selectedChannel.settings) === null || _a === void 0 ? void 0 : _a.tracking) === null || _b === void 0 ? void 0 : _b.fca) === null || _c === void 0 ? void 0 : _c.accessToken) &&
        ((_f = (_e = (_d = selectedChannel.settings) === null || _d === void 0 ? void 0 : _d.tracking) === null || _e === void 0 ? void 0 : _e.fca) === null || _f === void 0 ? void 0 : _f.pixelId)) {
        token = (_j = (_h = (_g = selectedChannel.settings) === null || _g === void 0 ? void 0 : _g.tracking) === null || _h === void 0 ? void 0 : _h.fca) === null || _j === void 0 ? void 0 : _j.accessToken;
        pixelId = (_m = (_l = (_k = selectedChannel.settings) === null || _k === void 0 ? void 0 : _k.tracking) === null || _l === void 0 ? void 0 : _l.fca) === null || _m === void 0 ? void 0 : _m.pixelId;
    }
    return {
        token,
        pixelId
    };
};
const getGoogleAnalytics = (shopConfig, selectedChannel) => {
    var _a;
    const { additionalGtagTrackingIds, useTrackingConsentAPI } = shopConfig;
    const { ga, gtm, ga4 } = ((_a = selectedChannel.settings) === null || _a === void 0 ? void 0 : _a.tracking) || initialAnalyticsConfig;
    return {
        ga: Object.assign(Object.assign({}, ga), { additionalGtagTrackingIds }),
        gtm,
        ga4,
        useTrackingConsentAPI
    };
};
const AnalyticsProvider = function AnalyticsProvider(props) {
    const shopConfig = useShopConfig();
    const { selectedChannel } = useContext(ChannelContext);
    const consentedToTracking = props.cookies.get('cookieConsent');
    const config = getGoogleAnalytics(shopConfig, selectedChannel);
    const { pixelId, token } = getFacebookCapi(selectedChannel);
    const trackingAllowed = allowsTrackingWithDNT();
    const analyticsConfig = React.useMemo(() => ({
        ga: {
            trackingId: config.ga.trackingId,
            additionalGtagTrackingIds: config.ga.additionalGtagTrackingIds,
            useArticleNumberAsId: config.ga.useArticleNumberAsId
        },
        ga4: {
            trackingId: config.ga4.trackingId,
            useArticleNumberAsId: config.ga4.useArticleNumberAsId
        },
        gtm: {
            containerId: config.gtm.containerId,
            useGa4Events: config.gtm.useGa4Events
        },
        releware: props.releware,
        trackingAllowed: trackingAllowed,
        consentedToTracking: consentedToTracking === 'agreed',
        cookies: props.cookies,
        useTrackingConsentAPI: config.useTrackingConsentAPI
    }), [
        config.ga.trackingId,
        config.ga.additionalGtagTrackingIds,
        config.ga.useArticleNumberAsId,
        config.ga4.trackingId,
        config.ga4.useArticleNumberAsId,
        config.gtm.containerId,
        config.gtm.useGa4Events,
        config.useTrackingConsentAPI,
        props.releware,
        props.cookies,
        trackingAllowed,
        consentedToTracking
    ]);
    const { gtm, ga, ga4, releware } = analyticsConfig;
    const registry = useMemo(() => trackerRegistry({
        config: analyticsConfig,
        inServer: __IN_SERVER__
    }), [analyticsConfig]);
    if (ga.trackingId) {
        registry.add(gtagTracker({
            currency: selectedChannel.currency.format.code,
            trackingId: ga.trackingId,
            additionalGtagTrackingIds: ga.additionalGtagTrackingIds,
            brandCustomField: shopConfig.brandCustomField,
            useArticleNumberAsId: ga.useArticleNumberAsId
        }));
    }
    else if (ga.trackingId.startsWith('G-')) {
        registry.add(ga4Tracker({
            currency: selectedChannel.currency.format.code,
            trackingId: ga.trackingId,
            additionalGtagTrackingIds: [],
            brandCustomField: shopConfig.brandCustomField,
            useArticleNumberAsId: ga.useArticleNumberAsId
        }));
    }
    if (ga4.trackingId) {
        registry.add(ga4Tracker({
            currency: selectedChannel.currency.format.code,
            trackingId: ga4.trackingId,
            additionalGtagTrackingIds: [],
            brandCustomField: shopConfig.brandCustomField,
            useArticleNumberAsId: ga4.useArticleNumberAsId
        }));
    }
    if (gtm.containerId) {
        referrerModifier(ga.trackingId || ga4.trackingId);
        registry.add(gtmTracker({
            containerId: gtm.containerId,
            currency: selectedChannel.currency.format.code,
            brandCustomField: shopConfig.brandCustomField,
            useArticleNumberAsId: ga.useArticleNumberAsId || ga4.useArticleNumberAsId
        }));
        const registred = registry.trackers();
        const ga4Registred = registred.includes('ga4');
        if (gtm.useGa4Events && !ga4Registred) {
            registry.add(ga4Tracker({
                currency: selectedChannel.currency.format.code,
                trackingId: gtm.containerId,
                additionalGtagTrackingIds: [],
                brandCustomField: shopConfig.brandCustomField,
                useArticleNumberAsId: ga4.useArticleNumberAsId,
                useGa4EventsFromGtm: gtm.useGa4Events
            }));
        }
    }
    // What do we do about this?
    // This is something we did for B***an* so that they'd have GTAG events in GTM,
    // this can safely be removed since they already have a 'dummy-UA' added in admin (we can run everything in paralell now)
    // const registred = registry.trackers();
    // if (!registred.includes('ga4') && !registred.includes('gtag')) {
    //   registry.add(
    //     gtagTracker({
    //       currency: selectedChannel.currency.format.code,
    //       trackingId: gtm.containerId,
    //       additionalGtagTrackingIds: [],
    //       brandCustomField: shopConfig.brandCustomField,
    //       useArticleNumberAsId:
    //         ga.useArticleNumberAsId || ga4.useArticleNumberAsId
    //     })
    //   );
    // }
    if (releware === null || releware === void 0 ? void 0 : releware.enabled) {
        registry.add(relewareTracker(releware));
    }
    if (shopConfig.trackers) {
        shopConfig.trackers.forEach((tracker) => {
            registry.add(tracker);
        });
    }
    if (shopConfig.serverTrackers) {
        registry.add(createServerTracker({
            currency: selectedChannel.currency.format.code,
            useArticleNumberAsId: ga.useArticleNumberAsId || ga4.useArticleNumberAsId,
            pixelId,
            token
        }));
    }
    const value = useMemo(() => {
        return {
            config: analyticsConfig,
            trackerRegistry: registry
        };
    }, [analyticsConfig, registry]);
    const preconnectLinks = usePreconnectLinks({
        ga: consentedToTracking && (!!(ga === null || ga === void 0 ? void 0 : ga.trackingId) || !!(ga4 === null || ga4 === void 0 ? void 0 : ga4.trackingId)),
        gtm: consentedToTracking && !!(gtm === null || gtm === void 0 ? void 0 : gtm.containerId)
    });
    return (React.createElement(AnalyticsContext.Provider, { value: value },
        React.createElement(Helmet, null, preconnectLinks.map(({ href, crossOrigin }) => (React.createElement("link", Object.assign({ rel: "preconnect", href: href }, (crossOrigin && { crossOrigin }), { key: href }))))),
        props.children));
};
function allowsTrackingWithDNT() {
    if (typeof window === 'undefined')
        return false;
    const dnt = window.doNotTrack ||
        navigator.doNotTrack ||
        // Support for IE 10 and older
        navigator.msDoNotTrack;
    if (dnt === '1' || dnt === 'yes') {
        return false;
    }
    // All versions of IE
    if (window.external && isMSExternal(window.external)) {
        return !window.external.msTrackingProtectionEnabled();
    }
    return true;
}
export default AnalyticsProvider;
//# sourceMappingURL=AnalyticsProvider.js.map